import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Stack,
    Switch,
    Typography,
} from '@mui/joy';
import { CheckCircle, Save, Sync, Warning } from '@mui/icons-material';
import { PageStandard } from '../utils/PageStandard';
import { AppSettings, getSettings, setAppVersion, setMaintenance } from '../utils/ViewModel';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface FieldState {
    value: string;
    saving: SaveState;
}

export function Settings() {
    const [loading, setLoading] = useState(true);
    const [maintenance, setMaintenanceLocal] = useState(false);
    const [maintenanceSaving, setMaintenanceSaving] = useState<SaveState>('idle');
    const [bulkSaving, setBulkSaving] = useState<SaveState>('idle');

    const [fields, setFields] = useState<Record<'android' | 'ios', FieldState>>({
        android: { value: '', saving: 'idle' },
        ios: { value: '', saving: 'idle' },
    });

    useEffect(() => {
        load();
    }, []);

    async function load() {
        setLoading(true);
        const data: AppSettings | null = await getSettings();
        if (data) {
            setFields({
                android: { value: data.android, saving: 'idle' },
                ios: { value: data.ios, saving: 'idle' },
            });
            setMaintenanceLocal(data.maintenance);
        }
        setLoading(false);
    }

    function updateField(key: 'android' | 'ios', value: string) {
        setFields(prev => ({ ...prev, [key]: { ...prev[key], value } }));
    }

    async function saveField(key: 'android' | 'ios') {
        setFields(prev => ({ ...prev, [key]: { ...prev[key], saving: 'saving' } }));
        const ok = await setAppVersion(key, fields[key].value.trim());
        setFields(prev => ({ ...prev, [key]: { ...prev[key], saving: ok ? 'saved' : 'error' } }));
        if (ok) setTimeout(() => setFields(prev => ({ ...prev, [key]: { ...prev[key], saving: 'idle' } })), 2000);
    }

    async function saveMaintenanceToggle(active: boolean) {
        setMaintenanceSaving('saving');
        const ok = await setMaintenance(active);
        if (ok) {
            setMaintenanceLocal(active);
            setMaintenanceSaving('saved');
            setTimeout(() => setMaintenanceSaving('idle'), 2000);
        } else {
            setMaintenanceSaving('error');
        }
    }

    async function saveAll() {
        setBulkSaving('saving');
        const results = await Promise.all([
            setAppVersion('android', fields.android.value.trim()),
            setAppVersion('ios', fields.ios.value.trim()),
            setMaintenance(maintenance),
        ]);
        const allOk = results.every(Boolean);
        setBulkSaving(allOk ? 'saved' : 'error');
        if (allOk) setTimeout(() => setBulkSaving('idle'), 2000);
    }

    if (loading) {
        return (
            <PageStandard title="Settings" sidebarVisible>
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                    <CircularProgress />
                </Box>
            </PageStandard>
        );
    }

    return (
        <PageStandard title="Settings" sidebarVisible>
            <Stack spacing={3} sx={{ maxWidth: 600 }}>

                {/* App Versions */}
                <Card variant="outlined">
                    <CardContent>
                        <Typography level="title-md" sx={{ mb: 2 }}>
                            Versioni minime app
                        </Typography>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
                            Se la versione installata dall'utente è inferiore a quella impostata,
                            l'app mostrerà la schermata di aggiornamento obbligatorio.
                        </Typography>

                        <Stack spacing={2}>
                            {(['android', 'ios'] as const).map(key => (
                                <FormControl key={key}>
                                    <FormLabel sx={{ textTransform: 'capitalize' }}>{key}</FormLabel>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Input
                                            value={fields[key].value}
                                            onChange={e => updateField(key, e.target.value)}
                                            placeholder="es. 1.0.0"
                                            sx={{ flex: 1 }}
                                        />
                                        <Button
                                            size="sm"
                                            variant="soft"
                                            color={
                                                fields[key].saving === 'saved' ? 'success' :
                                                fields[key].saving === 'error' ? 'danger' : 'primary'
                                            }
                                            loading={fields[key].saving === 'saving'}
                                            startDecorator={
                                                fields[key].saving === 'saved' ? <CheckCircle fontSize="small" /> :
                                                fields[key].saving === 'error' ? <Warning fontSize="small" /> :
                                                <Save fontSize="small" />
                                            }
                                            onClick={() => saveField(key)}
                                        >
                                            {fields[key].saving === 'saved' ? 'Salvato' :
                                             fields[key].saving === 'error' ? 'Errore' : 'Salva'}
                                        </Button>
                                    </Stack>
                                    <FormHelperText>
                                        Formato semver: 1.2.3
                                    </FormHelperText>
                                </FormControl>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>

                {/* Maintenance */}
                <Card variant="outlined">
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                                <Typography level="title-md">Manutenzione</Typography>
                                <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                                    Se attivo, l'app mostra la schermata di manutenzione a tutti gli utenti.
                                </Typography>
                            </Box>
                            <Chip
                                size="sm"
                                color={maintenance ? 'danger' : 'success'}
                                variant="soft"
                                sx={{ ml: 2, mt: 0.5 }}
                            >
                                {maintenance ? 'Attiva' : 'Disattiva'}
                            </Chip>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Switch
                                checked={maintenance}
                                onChange={e => saveMaintenanceToggle(e.target.checked)}
                                disabled={maintenanceSaving === 'saving'}
                                color={maintenance ? 'danger' : 'neutral'}
                            />
                            <Typography level="body-sm">
                                {maintenanceSaving === 'saving' ? 'Salvataggio…' :
                                 maintenanceSaving === 'saved' ? '✓ Salvato e propagato' :
                                 maintenanceSaving === 'error' ? '✗ Errore' :
                                 maintenance ? 'Manutenzione attiva' : 'App operativa'}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* Bulk save */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="plain"
                        color="neutral"
                        startDecorator={<Sync />}
                        onClick={load}
                    >
                        Ricarica
                    </Button>
                    <Button
                        color={
                            bulkSaving === 'saved' ? 'success' :
                            bulkSaving === 'error' ? 'danger' : 'primary'
                        }
                        loading={bulkSaving === 'saving'}
                        startDecorator={
                            bulkSaving === 'saved' ? <CheckCircle /> :
                            bulkSaving === 'error' ? <Warning /> :
                            <Save />
                        }
                        onClick={saveAll}
                    >
                        {bulkSaving === 'saved' ? 'Tutto salvato' :
                         bulkSaving === 'error' ? 'Errore parziale' :
                         'Salva tutto'}
                    </Button>
                </Box>
            </Stack>
        </PageStandard>
    );
}
